import { Component, Input, model, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { simpleDeepClone } from '@phx-erp/shared';
import { distinctUntilChanged, map } from 'rxjs';
import { WidgetBaseDetailComponent } from '../../../../shared/components/widget-base-detail/widget-base-detail.component';
import { Dialog } from "../../../../@core/services/modal/modal.interfaces";
import { BaseDetailComponent } from '../../../../common/base-detail.component';
import { Address, CustomerPriceList, CustomerPriceListAddressLink, Operator, TaxType } from '../../../../generated/generated-types';
import { HotkeysService } from '../../../hotkeys/services/hotkeys.service';
import { CustomerPriceListAddressLinkService } from '../../services/customer-price-list-address-link.service';
import { CustomerPriceListService } from '../../services/customer-price-list.service';
import { CustomerPriceListDetailFallbackFields } from './fallback';

@Component({
  selector: 'app-customer-price-list-detail-widget',
  templateUrl: '../../../../common/base-detail.component.html',
  standalone: true,
  providers: [HotkeysService],
  imports: [
    WidgetBaseDetailComponent
  ]
})
export class CustomerPriceListDetailWidgetComponent extends BaseDetailComponent<CustomerPriceListAddressLink> implements OnInit, Dialog {

  productId = model<string>();
  @Input() address: Address;
  addressIdCached: string;

  protected route = inject(ActivatedRoute);
  protected router = inject(Router);
  private customerPriceListService = inject(CustomerPriceListService);
  private customerPriceListAddressLinkService = inject(CustomerPriceListAddressLinkService);
  protected override crudService = this.customerPriceListAddressLinkService;

  constructor() {
    super();
    this.destroyRef.onDestroy(() => {
      console.log(`Destroying ${this.constructor.name}...`);
    });
  }

  resolveWith: (result?: CustomerPriceListAddressLink) => void;

  ngOnInit() {
    this.actionBarLocationId = 'customer-price-list-group-detail';
    this.fallbackFields = CustomerPriceListDetailFallbackFields();

    if (this.detailModel.customerPriceList == null)
      this.detailModel.customerPriceList = {} as CustomerPriceList;

    super.ngOnInit();
  }

  override buildForm(): void {
    super.buildForm();

    this.detailForm.controls["address"].valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).pipe(distinctUntilChanged()).subscribe(async address => {
      if (address && this.addressIdCached !== address.id) {
        this.addressIdCached = address.id;
        this.setLoading();

        let newModel = simpleDeepClone(this.detailModel);

        const existingSpecificLink: CustomerPriceListAddressLink[] = (await this.customerPriceListAddressLinkService.getItems({
          take: 1,
          searchFilter: {
            rules: [
              { field: "addressId", operator: Operator.equal, value: address.id },
              { field: "productId", operator: Operator.equal, value: this.productId() }
            ]
          }

        }).single$.pipe(map(x => x.getCustomerPriceListAddressLinks)).toPromise()).items as CustomerPriceListAddressLink[];

        if (existingSpecificLink?.length > 0) { //reset cause its a duplicate
          newModel = {} as CustomerPriceListAddressLink;
          this.baseServices.notificationService.warning(_("products.customer-pricelist-address-already-linked"));
          this.addressIdCached = null;
          //TODO refactor detailModel logic, needs both here otherwise not works in all cases
          this.detailModelSig.set(newModel);
          this.detailModel = newModel;
          this.detailForm.reset();
          this.cdRef.markForCheck();
          this.setLoading(false);
          return;
        }

        //check if there is already links (doesnt matter which one) and priceList for this address and product to set currency & priceIncludesTax
        const anyExistingLinksToAddress: CustomerPriceListAddressLink[] = (await this.customerPriceListAddressLinkService.getItems({ take: 1, searchFilter: { field: "addressId", operator: Operator.equal, value: address.id } }).single$.pipe(map(x => x.getCustomerPriceListAddressLinks)).toPromise()).items as CustomerPriceListAddressLink[];
        let existingCustomerPriceList: CustomerPriceList = {} as CustomerPriceList;
        if (anyExistingLinksToAddress?.length > 0)
          existingCustomerPriceList = await this.customerPriceListService.getItemData(anyExistingLinksToAddress[0].customerPriceListId); // not necessary all in link
        let priceIncludesTax: boolean;
        //otherwise its a reference and readonly
        newModel.customerPriceList = simpleDeepClone(existingCustomerPriceList);

        if (!newModel.customerPriceList?.id) {
          priceIncludesTax = address.debitor?.taxType === TaxType.GROSS;
          newModel.customerPriceList.priceIncludesTax = priceIncludesTax;
          newModel.customerPriceList.currency = address.debitor?.currency;
        }

        if (!newModel.customerPriceList.currency)
          newModel.customerPriceList.currency = this.tenantService.getTenantCurrency();

        this.detailModelSig.set(newModel);
        this.detailForm.patchValue(newModel, { emitEvent: true });
        this.setLoading(false);
      }
      this.cdRef.markForCheck();
    });
  }

  async save(detailModel: CustomerPriceListAddressLink) {

    detailModel = detailModel ?? this.detailModel;
    detailModel.productId = this.productId();
    if (!detailModel.address)
      detailModel.address = this.address;
    try {
      let saved = await super.save(detailModel);

      await this.setModel({} as CustomerPriceListAddressLink);
      if (this.resolveWith)
        this.resolveWith(saved);
      return saved
    }
    catch (e: any) {
      this.setLoading(false);
    }
  }

}
